import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuAlertTriangle, LuBellRing, LuPause, LuSearch } from 'react-icons/lu';
import { Button, LoadingSpinner } from 'components/common';
import { listReminders } from './remindersApi';
import { formatInZone } from './reminderFormat';
import ReminderSettingsCard from './ReminderSettingsCard';
import { SchedulerHealthBanner, needsAttention } from './ReminderHealth';

// App Settings → Reminders tab: every scheduled reminder of the Portal (backend
// Reminders::Registry), grouped by module, then the settings of the one selected
// (kept in the URL by the caller, so "Settings → Reminders → Payment requests" is a
// link to send). A module that registers a reminder shows up here with no UI work.

// Search only earns its place once the list no longer fits at a glance.
const SEARCH_FROM = 6;

const FILTERS = [
  { key: 'all', label: 'All', test: () => true },
  { key: 'on', label: 'On', test: (r) => r.enabled },
  { key: 'off', label: 'Off', test: (r) => !r.enabled },
  { key: 'attention', label: 'Needs attention', test: needsAttention },
];

const ReminderListItem = ({ reminder, active, onSelect }) => {
  const failed = reminder.last_run?.status === 'failed';
  const missed = (reminder.missed_today || []).length > 0;
  return (
    <button type="button" onClick={onSelect} aria-current={active ? 'true' : undefined}
      className={`tw-w-full tw-text-left tw-px-3 tw-py-2.5 tw-rounded-lg tw-border tw-border-solid tw-cursor-pointer tw-transition-colors ${
        active ? 'tw-bg-primary-50 tw-border-primary-200' : 'tw-bg-white tw-border-slate-200 hover:tw-bg-slate-50'}`}>
      <span className="tw-flex tw-items-center tw-gap-2">
        <span className={`tw-flex-1 tw-min-w-0 tw-truncate tw-text-[13px] tw-font-semibold ${active ? 'tw-text-primary-800' : 'tw-text-slate-800'}`}>
          {reminder.label}
        </span>
        {reminder.kind === 'task' && (
          <span className="tw-shrink-0 tw-px-1.5 tw-py-px tw-rounded tw-bg-slate-100 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">Task</span>
        )}
        {(failed || missed) && <LuAlertTriangle size={12} className="tw-text-red-600 tw-shrink-0" aria-label="Needs attention" />}
        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-font-semibold tw-shrink-0 ${reminder.enabled ? 'tw-text-emerald-700' : 'tw-text-slate-400'}`}>
          {reminder.enabled ? <LuBellRing size={12} /> : <LuPause size={12} />}{reminder.enabled ? 'On' : 'Off'}
        </span>
      </span>
      <span className="tw-block tw-text-[11px] tw-text-slate-500">
        {reminder.next_run ? `Next ${formatInZone(reminder.next_run.at, reminder.time_zone, true)} (${reminder.zone_label})` : 'Not scheduled'}
        {failed ? ' · last run failed' : ''}
        {missed ? ' · missed today' : ''}
      </span>
    </button>
  );
};
ReminderListItem.propTypes = { reminder: PropTypes.object.isRequired, active: PropTypes.bool, onSelect: PropTypes.func.isRequired };

// [[group, reminders]] in the order the server lists them.
const byGroup = (reminders) => reminders.reduce((groups, r) => {
  const found = groups.find(([name]) => name === r.group);
  return found
    ? groups.map((g) => (g === found ? [g[0], [...g[1], r]] : g))
    : [...groups, [r.group, [r]]];
}, []);

const RemindersOverview = ({ selectedKey, onSelect }) => {
  const [reminders, setReminders] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await listReminders();
      setReminders(res.data);
      setScheduler(res.scheduler || null);
    }
    catch (e) { setError(e?.response?.data?.error || 'Could not load the reminders.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const test = FILTERS.find((f) => f.key === filter).test;
    const q = query.trim().toLowerCase();
    return (reminders || []).filter((r) => test(r) &&
      (!q || `${r.label} ${r.group} ${r.description}`.toLowerCase().includes(q)));
  }, [reminders, filter, query]);

  if (!reminders) {
    return (
      <div className="tw-py-16 tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-sm tw-text-slate-500">
        {error ? <><span role="alert">{error}</span><Button variant="secondary" size="sm" onClick={load}>Retry</Button></> : <LoadingSpinner />}
      </div>
    );
  }

  // The selection stays open even when a filter hides it from the list.
  const active = reminders.find((r) => r.key === selectedKey) || reminders[0];
  const count = (f) => reminders.filter(f.test).length;

  return (
    <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4 tw-items-start">
      <nav aria-label="Reminders" className="tw-w-full md:tw-w-72 tw-shrink-0 tw-flex tw-flex-col tw-gap-3 md:tw-sticky md:tw-top-4">
        <SchedulerHealthBanner scheduler={scheduler} />
        <p className="tw-m-0 tw-text-[12px] tw-text-slate-500">
          Scheduled emails that remind people of work waiting for them.
        </p>

        {reminders.length >= SEARCH_FROM && (
          <label className="tw-relative tw-block">
            <span className="tw-sr-only">Search reminders</span>
            <LuSearch size={13} className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search reminders"
              className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-[13px] tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white" />
          </label>
        )}

        <div role="group" aria-label="Filter reminders" className="tw-flex tw-flex-wrap tw-gap-1">
          {FILTERS.map((f) => {
            const n = count(f);
            if (f.key === 'attention' && n === 0) return null;
            const on = filter === f.key;
            return (
              <button key={f.key} type="button" aria-pressed={on} onClick={() => setFilter(f.key)}
                className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-border tw-border-solid tw-text-[12px] tw-font-semibold tw-cursor-pointer ${
                  on ? 'tw-bg-slate-900 tw-border-slate-900 tw-text-white'
                    : f.key === 'attention' ? 'tw-bg-red-50 tw-border-red-200 tw-text-red-700'
                      : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-50'}`}>
                {f.label} <span className="tw-tabular-nums tw-opacity-70">{n}</span>
              </button>
            );
          })}
        </div>

        {visible.length === 0 && <p className="tw-m-0 tw-text-[13px] tw-text-slate-500">No reminder matches.</p>}
        {byGroup(visible).map(([group, items]) => (
          <section key={group} aria-label={group} className="tw-flex tw-flex-col tw-gap-1.5">
            <h3 className="tw-m-0 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-400">{group}</h3>
            {items.map((r) => (
              <ReminderListItem key={r.key} reminder={r} active={r.key === active?.key} onSelect={() => onSelect(r.key)} />
            ))}
          </section>
        ))}
      </nav>

      <div className="tw-flex-1 tw-min-w-0 tw-w-full">
        {active
          ? <ReminderSettingsCard key={active.key} reminderKey={active.key} onSaved={load} />
          : <p className="tw-text-sm tw-text-slate-500">No reminder is registered yet.</p>}
      </div>
    </div>
  );
};

RemindersOverview.propTypes = {
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default RemindersOverview;
